import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { TablePlugin as LexicalTablePlugin } from '@lexical/react/LexicalTablePlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import {
  $getRoot,
  $insertNodes,
  $isTextNode,
  DOMConversionMap,
  DOMConversionOutput,
  EditorState,
  EditorThemeClasses,
  isHTMLElement,
  LexicalEditor,
  SKIP_DOM_SELECTION_TAG,
  TextNode,
} from 'lexical';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import {
  getCSSFromStyleObject,
  getStyleObjectFromCSS,
} from '@lexical/selection';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { RefObject, useEffect, useRef } from 'react';
import { ToolbarPlugin } from './toolbarPlugin';
import { TableCellSelectionPlugin } from './plugins/TableCellSelectionPlugin';
import { TableCellResizerPlugin } from './plugins/TableCellResizerPlugin';
import { DefaultBoldFormatPlugin } from './plugins';
import { TemplatePlugin } from './plugins/TemplatePlugin';
import { PlaceholderSuggestionsPlugin } from './plugins/PlaceholderSuggestionsPlugin';
import { PlaceholderNode } from './nodes/PlaceholderNode';
import { TextTemplate } from './textTemplates';
import { richTextEditorStrings } from '../../i18n/components/richtTextEditor';
import './styles.css';

const theme: EditorThemeClasses = {
  paragraph: 'lexical-paragraph',
  quote: 'lexical-quote',
  heading: {
    h1: 'lexical-heading-h1',
    h2: 'lexical-heading-h2',
    h3: 'lexical-heading-h3',
    h4: 'lexical-heading-h4',
    h5: 'lexical-heading-h5',
    h6: 'lexical-heading-h6',
  },
  list: {
    nested: {
      listitem: 'lexical-nested-listitem',
    },
    ol: 'lexical-list-ol',
    ul: 'lexical-list-ul',
    listitem: 'lexical-listItem',
  },
  link: 'lexical-link',
  text: {
    bold: 'lexical-text-bold',
    italic: 'lexical-text-italic',
    underline: 'lexical-text-underline',
    strikethrough: 'lexical-text-strikethrough',
    underlineStrikethrough: 'lexical-text-underline-strikethrough',
    code: 'lexical-text-code',
  },
  table: 'lexical-table',
  tableCell: 'lexical-table-cell',
  tableCellHeader: 'lexical-table-cell-header',
};

const IMPORTED_TEXT_STYLES = ['color', 'background-color'];

// Lexical's default text import drops inline colors; this carries them onto the text nodes.
function withTextStyle(
  output: DOMConversionOutput | null,
  element: Node,
): DOMConversionOutput | null {
  if (output === null || !isHTMLElement(element)) {
    return output;
  }

  const style: Record<string, string> = {};
  for (const property of IMPORTED_TEXT_STYLES) {
    const value = element.style.getPropertyValue(property);
    if (value) {
      style[property] = value;
    }
  }
  if (Object.keys(style).length === 0) {
    return output;
  }

  const { forChild } = output;
  return {
    ...output,
    forChild: (child, parent) => {
      const node = forChild ? forChild(child, parent) : child;
      if ($isTextNode(node)) {
        node.setStyle(
          getCSSFromStyleObject({
            ...getStyleObjectFromCSS(node.getStyle()),
            ...style,
          }),
        );
      }
      return node;
    },
  };
}

const htmlImport: DOMConversionMap = Object.fromEntries(
  Object.entries(TextNode.importDOM() ?? {}).map(([tag, importer]) => [
    tag,
    (node: HTMLElement) => {
      const conversion = importer(node);
      return (
        conversion && {
          ...conversion,
          conversion: (element: HTMLElement) =>
            withTextStyle(conversion.conversion(element), element),
        }
      );
    },
  ]),
);

export interface RichTextEditorProps {
  namespace: string;
  onChange?: (html: string) => void;
  onError?: (error: Error, editor: LexicalEditor) => void;
  placeholder?: string;
  ariaLabel?: string;
  value?: string;
  readOnly?: boolean;
  defaultBold?: boolean;
  templates?: TextTemplate[];
  templateValues?: Record<string, string>;
  templateOptions?: Record<string, string[]>;
}

function defaultOnError(error: Error) {
  console.error(error);
}

function HtmlValuePlugin({
  value,
  emittedRef,
}: {
  value?: string;
  emittedRef: RefObject<string | null>;
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Skip what this editor reported itself: that content is already in the
    // document, and re-importing it would reset the caret on every keystroke.
    if (!value || value === emittedRef.current) {
      return;
    }

    editor.update(
      () => {
        const root = $getRoot();
        root.clear();

        const parser = new DOMParser();
        const dom = parser.parseFromString(value, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);
        $insertNodes(nodes);
      },
      // $insertNodes leaves a selection behind, which Lexical would apply to the
      // DOM: that focuses the editor and scrolls it into view. Skipping it keeps
      // the caret out of a body the user has not clicked into.
      { tag: SKIP_DOM_SELECTION_TAG },
    );
  }, [editor, value, emittedRef]);

  return null;
}

function HtmlChangePlugin({
  onChange,
  emittedRef,
}: {
  onChange: (html: string) => void;
  emittedRef: RefObject<string | null>;
}) {
  const [editor] = useLexicalComposerContext();

  const handleChange = (editorState: EditorState) => {
    editorState.read(() => {
      const html = $generateHtmlFromNodes(editor, null);
      emittedRef.current = html;
      onChange(html);
    });
  };

  return <OnChangePlugin onChange={handleChange} />;
}

export function RichTextEditor({
  onChange,
  onError,
  namespace,
  placeholder = richTextEditorStrings.placeholder.default,
  ariaLabel,
  value,
  readOnly = false,
  defaultBold = false,
  templates,
  templateValues,
  templateOptions,
}: RichTextEditorProps) {
  // The HTML this editor last reported, so a value coming back in is recognised
  // as an echo instead of a new document to import.
  const emittedRef = useRef<string | null>(null);

  const initialConfig = {
    namespace,
    theme,
    onError: onError ?? defaultOnError,
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      LinkNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      PlaceholderNode,
    ],
    editable: !readOnly,
    html: { import: htmlImport },
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div
        className={`lexical-editor-container ${readOnly ? 'lexical-editor-readonly' : ''}`}
      >
        {!readOnly && (
          <ToolbarPlugin
            templates={templates}
            templateValues={templateValues}
          />
        )}
        <div className="lexical-editor-inner">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="lexical-content-editable"
                aria-placeholder={placeholder}
                aria-label={ariaLabel}
                placeholder={(isEditable) =>
                  isEditable && !readOnly ? (
                    <div className="lexical-placeholder">{placeholder}</div>
                  ) : null
                }
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HtmlValuePlugin value={value} emittedRef={emittedRef} />
          {!readOnly && onChange && (
            <HtmlChangePlugin onChange={onChange} emittedRef={emittedRef} />
          )}
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <LexicalTablePlugin />
          {!readOnly && <TableCellSelectionPlugin />}
          {!readOnly && <TableCellResizerPlugin />}
          {!readOnly && defaultBold && <DefaultBoldFormatPlugin />}
          {!readOnly && templates && <TemplatePlugin />}
          {!readOnly && templates && templateOptions && (
            <PlaceholderSuggestionsPlugin options={templateOptions} />
          )}
          <TabIndentationPlugin />
        </div>
      </div>
    </LexicalComposer>
  );
}
